import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  CheckDuplicatePartnerCodeQuery,
  CreatePartnerData,
  ExportPartnerBody,
  FindAvailableEntityForUpdateChainEntityOption,
  FindOptions,
  FindPartnerEntityPolicyQuery,
  FindPartnerForCreateCentralEntityPolicy,
  FindPartnerForUpdateEntityPolicyQuery,
  FindSupplierPartnerForCreateDigitalMenuBodyDto,
  GeneratePartnerCodeQuery,
  PartnerApi,
  SyncProductsInChainBody,
  UpdateEntityPolicyMultiBody,
  UpdateForecastSettingBody,
  UpdatePartnerIsCalculatorDebtBody,
  UpdatePartnerNoteBody,
  UpdatePartnerShouldForecastQuantityBody,
} from 'api';
import { PARTNER_IN_CHAIN_TYPE } from 'utils';

export const findOnePartner = createAsyncThunk(
  'partner/findOnePartner',
  async (body: { partnerID: string; option: any }, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.findOne(body.partnerID, body.option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findOnePartnerNote = createAsyncThunk(
  'partner/findPartnerNote',
  async (body: { partnerID: string; option: any }, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.findOne(body.partnerID, body.option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findPartner = createAsyncThunk<
  any,
  any,
  { rejectValue: { message: string } }
>('partner/findPartner', async (option: any, { rejectWithValue }) => {
  try {
    const response = await PartnerApi.find(option);

    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const findPartnerForForecastQuantity = createAsyncThunk<
  any,
  any,
  { rejectValue: { message: string } }
>(
  'partner/findPartnerForForecastQuantity',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.findPartnerForForecastQuantity(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findCustomerPartner = createAsyncThunk<
  any,
  any,
  { rejectValue: { message: string } }
>('partner/findCustomerPartner', async (option: any, { rejectWithValue }) => {
  try {
    const { entityIDs, groupIDs, ...query } = option;

    const response = await PartnerApi.findCustomerPartner(query, {
      entityIDs,
      groupIDs,
    });

    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const updatePartnerType = createAsyncThunk<
  any,
  any,
  { rejectValue: { message: string } }
>(
  'partner/updatePartnerType',
  async (params: { id: string; partnerType: number }, { rejectWithValue }) => {
    try {
      const { id, partnerType } = params;
      const response = await PartnerApi.updatePartnerType({ partnerType }, id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findBranchPartner = createAsyncThunk<
  any,
  any,
  { rejectValue: { message: string } }
>('partner/findBranchPartner', async (option: any, { rejectWithValue }) => {
  try {
    const { entityIDs, groupIDs, ...query } = option;
    const response = await PartnerApi.findBranchPartner(query, {
      entityIDs,
      groupIDs,
    });

    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const findSupplierPartner = createAsyncThunk<
  any,
  any,
  { rejectValue: { message: string } }
>('partner/findSupplierPartner', async (option: any, { rejectWithValue }) => {
  try {
    const { entityIDs, groupIDs, ...query } = option;
    const response = await PartnerApi.findSupplierPartner(query, {
      entityIDs,
      groupIDs,
    });

    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const findWarehousePartner = createAsyncThunk<
  any,
  any,
  { rejectValue: { message: string } }
>('partner/findWarehousePartner', async (option: any, { rejectWithValue }) => {
  try {
    const { entityIDs, groupIDs, ...query } = option;
    const response = await PartnerApi.findWarehousePartner(query, {
      entityIDs,
      groupIDs,
    });

    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const findPartnerForEntityShippingList = createAsyncThunk<
  any,
  any,
  { rejectValue: { message: string } }
>(
  'partner/findPartnerForShippingList',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.findPartnerForShippingList(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findAllPartner = createAsyncThunk(
  'partner/findAllPartner',
  async (body: any, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.findAllPartner(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const checkPermissionEditWithPartner = createAsyncThunk(
  'partner/checkPermissionEditWithPartner',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.checkPermissionEditWithPartner(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findPartnerForModal = createAsyncThunk(
  'partner/findPartnerForModal',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.find(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findPartnerForMenuList = createAsyncThunk(
  'partner/findPartnerForMenuList',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.find(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findPartnerForTableList = createAsyncThunk(
  'partner/findPartnerForTableList',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.find(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findPartnerForForwardList = createAsyncThunk(
  'partner/findPartnerForForwardList',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.find(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updatePartner = createAsyncThunk(
  'partner/updatePartner',
  async (params: any, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await PartnerApi.update(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updatePartnerIsCalculatorDebt = createAsyncThunk(
  'partner/updatePartnerIsCalculatorDebt',
  async (body: UpdatePartnerIsCalculatorDebtBody, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.updatePartnerIsCalculatorDebt(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateForecastSettings = createAsyncThunk(
  'partner/updateForecastSettings',
  async (
    { id, body }: { id: string; body: UpdateForecastSettingBody },
    { rejectWithValue }
  ) => {
    try {
      const response = await PartnerApi.updateForecastSettings(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updatePartnerShouldForecastQuantity = createAsyncThunk(
  'partner/updatePartnerShouldForecastQuantity',
  async (
    { id, body }: { id: string; body: UpdatePartnerShouldForecastQuantityBody },
    { rejectWithValue }
  ) => {
    try {
      const response = await PartnerApi.updateShouldForecastQuantity(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updatePartnerNote = createAsyncThunk(
  'partner/updatePartnerNote',
  async (
    { id, body }: { id: string; body: UpdatePartnerNoteBody },
    { rejectWithValue }
  ) => {
    try {
      const response = await PartnerApi.updateNote(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateGroupMultiPartner = createAsyncThunk(
  'partner/updateGroupMultiPartner',
  async (
    body: { partnerIDs: string[]; groupID: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await PartnerApi.updateGroupMulti(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteGroupMultiPartner = createAsyncThunk(
  'partner/deleteGroupMultiPartner',
  async (body: { partnerIDs: string[] }, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.deleteGroupMulti(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateGroupPartner = createAsyncThunk(
  'partner/updateGroupPartner',
  async (body: { partnerID: string; groupID: string }, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.updateGroup(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateEntityPolicyMulti = createAsyncThunk(
  'partner/updateEntityPolicyMulti',
  async (body: UpdateEntityPolicyMultiBody, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.updateEntityPolicyMulti(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updatePartnerStatus = createAsyncThunk(
  'partner/updatePartnerStatus',
  async (params: any, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await PartnerApi.updateStatus(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const approvePartner = createAsyncThunk(
  'partner/approvePartner',
  async (body: any, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.approve(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createPartner = createAsyncThunk(
  'partner/createPartner',
  async (data: CreatePartnerData, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.create(data);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const removePartnerPricePolicy = createAsyncThunk(
  'partner/removePartnerPricePolicy',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.removePricePolicy(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const removePartnerPricePolicyMulti = createAsyncThunk(
  'partner/removePartnerPricePolicyMulti',
  async (body: { partnerIDs: string[] }, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.removePricePolicyMulti(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const removePartnerProposalPolicy = createAsyncThunk(
  'partner/removePartnerProposalPolicy',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.removeProposalPolicy(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const removePartnerProposalPolicyMulti = createAsyncThunk(
  'partner/removePartnerProposalPolicyMulti',
  async (body: { partnerIDs: string[] }, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.removeProposalPolicyMulti(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updatePartnerPolicy = createAsyncThunk(
  'partner/updatePartnerPolicy',
  async (params: any, { rejectWithValue }) => {
    const { id, body } = params;
    try {
      const response = await PartnerApi.updatePolicy(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const deletePartner = createAsyncThunk(
  'partner/deletePartner',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.delete(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteGroupOfPartner = createAsyncThunk(
  'partner/deleteGroupOfPartner',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.deleteGroup(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const syncProductsInChain = createAsyncThunk(
  'partner/syncProductsInChain',
  async (body: SyncProductsInChainBody, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.syncProductsInChain(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findToCheckDuplicatePartnerCode = createAsyncThunk(
  'partner/findToCheckDuplicatePartnerCode',
  async (
    query: { partnerCode: string; entityID: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await PartnerApi.findToCheckDuplicate(query);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findDebtProofInfoForPartner = createAsyncThunk(
  'partner/findDebtProofInfoForPartner',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.findDebtProofInfo(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findPartnerForCreateCentralEntityPolicy = createAsyncThunk(
  'partner/findPartnerForCreateCentralEntityPolicy',
  async (
    query: FindPartnerForCreateCentralEntityPolicy,
    { rejectWithValue }
  ) => {
    try {
      const response = await PartnerApi.findPartnerForCreateCentralEntityPolicy(
        query
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findPartnerForFullOrderQuantitySetting = createAsyncThunk(
  'partner/findPartnerForFullOrderQuantitySetting',
  async (
    query: FindPartnerForCreateCentralEntityPolicy,
    { rejectWithValue }
  ) => {
    try {
      const response = await PartnerApi.findPartnerForFullOrderQuantitySetting(
        query
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findPartnerForUpdateEntityPolicy = createAsyncThunk(
  'partner/findPartnerForUpdateEntityPolicy',
  async (query: FindPartnerForUpdateEntityPolicyQuery, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.findPartnerForUpdateEntityPolicy(query);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findChainEntityAndPartnerForUpdateEntityPolicy = createAsyncThunk(
  'partner/findChainEntityAndPartnerForUpdateEntityPolicy',
  async (query: FindPartnerForUpdateEntityPolicyQuery, { rejectWithValue }) => {
    try {
      const response =
        await PartnerApi.findChainEntityAndPartnerForUpdateEntityPolicy(query);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findPartnerEntityPolicy = createAsyncThunk(
  'partner/findPartnerEntityPolicy',
  async (query: FindPartnerEntityPolicyQuery, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.findPartnerEntityPolicy(query);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findChainEntityAndPartnerEntityPolicy = createAsyncThunk(
  'partner/findChainEntityAndPartnerEntityPolicy',
  async (query: FindPartnerEntityPolicyQuery, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.findChainEntityAndPartnerEntityPolicy(
        query
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findCustomerPartnerForCreateDigitalMenu = createAsyncThunk(
  'partner/findCustomerPartnerForCreateDigitalMenu',
  async (query: FindOptions, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.findCustomerPartnerForCreateDigitalMenu(
        query
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const exportPartner = createAsyncThunk(
  'partner/exportPartner',
  async (body: ExportPartnerBody, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.exportPartner(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const exportPartnerSupplier = createAsyncThunk(
  'partner/exportPartnerSupplier',
  async (_, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.exportPartnerSupplier();

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const exportPartnerCustomer = createAsyncThunk(
  'partner/exportPartnerCustomer',
  async (_, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.exportPartnerCustomer();

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const exportPartnerBranch = createAsyncThunk(
  'partner/exportPartnerBranch',
  async (_, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.exportPartnerBranch();

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const exportPartnerWarehouse = createAsyncThunk(
  'partner/exportPartnerWarehouse',
  async (_, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.exportPartnerWarehouse();

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findSupplierPartnerForCreateDigitalMenu = createAsyncThunk(
  'partner/findSupplierPartnerForCreateDigitalMenu',
  async (
    body: FindSupplierPartnerForCreateDigitalMenuBodyDto,
    { rejectWithValue }
  ) => {
    try {
      const response = await PartnerApi.findSupplierPartnerForCreateDigitalMenu(
        body
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findOnePartnerForCRM = createAsyncThunk(
  'partner/findOnePartnerForCRM',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.findOnePartnerForCRM(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findPartnersInGroup = createAsyncThunk(
  'partner/findPartnersInGroup',
  async (params: { option; id }, { rejectWithValue }) => {
    try {
      const { option, id } = params;
      const response = await PartnerApi.findPartnersInGroup(id, option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findPartnersForAddPartnerInGroup = createAsyncThunk(
  'partner/findPartnersForAddPartnerInGroup',
  async (params: { option; id }, { rejectWithValue }) => {
    try {
      const { option, id } = params;
      const response = await PartnerApi.findPartnersForAddPartnerInGroup(
        id,
        option
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findPartnerForCreateGroup = createAsyncThunk(
  'partner/findPartnerForCreateGroup',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.findPartnerForCreateGroup(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const generatePartnerCode = createAsyncThunk(
  'partner/generatePartnerCode',
  async (option: GeneratePartnerCodeQuery, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.generatePartnerCode(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findPartnerWaitingForApproveConnect = createAsyncThunk(
  'partner/findPartnerWaitingForApproveConnect',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.findPartnerWaitingForApproveConnect(
        option
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const checkDuplicatePartnerCode = createAsyncThunk(
  'partner/checkDuplicatePartnerCode',
  async (query: CheckDuplicatePartnerCodeQuery, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.checkDuplicate(query);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findAvailableEntityForUpdateChainEntity = createAsyncThunk(
  'partner/findAvailableEntityForUpdateChainEntity',
  async (
    query: FindAvailableEntityForUpdateChainEntityOption,
    { rejectWithValue }
  ) => {
    try {
      const response = await PartnerApi.findAvailableEntityForUpdateChainEntity(
        query
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findPartnerTypeInChain = createAsyncThunk(
  'partner/findPartnerTypeInChain',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.findPartnerTypeInChain(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateMultiSettingPartner = createAsyncThunk(
  'partner/updateMultiSettingPartner',
  async (body: any, { rejectWithValue }) => {
    try {
      const response = await PartnerApi.updateMultiSettingPartners(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

interface SelectedSettingPartner {
  entityPricePolicyID: string | undefined;
  entityProposalPolicyID: string | undefined;
  selectedProducts: any[];
  selectAllProducts: boolean;
  categoryID: string | undefined;
  search: string | undefined;
  entityPricePolicy: any;
  entityProposalPolicy: any;
  totalSelectedProduct: number;
  groupID: string;
}
interface IState {
  list: Array<any>;
  selectList: Array<any>;
  orderList: Array<any>;
  menuList: Array<any>;
  forwardList: Array<any>;
  reloadFlag: boolean;
  reloadFlagNotEffect?: boolean;
  selectedSuggestEntityInChainType: PARTNER_IN_CHAIN_TYPE;
  selectedPartnerType?: any;
  selectedChainEntity: any;
  entityPricePolicyList: any[];
  entityProposalPolicyList: any[];

  selectedSettingPartner: SelectedSettingPartner | undefined;
  selectedSettingApprovePartner: SelectedSettingPartner | undefined;
  isLoadingSuggestSelectedEntityType: boolean;
  isLoadingRecentlySelectedEntityType: boolean;
}

const initialState: IState = {
  list: [],
  selectList: [],
  orderList: [],
  menuList: [],
  forwardList: [],
  reloadFlag: true,
  reloadFlagNotEffect: undefined,
  selectedSuggestEntityInChainType: PARTNER_IN_CHAIN_TYPE.ALL,
  selectedPartnerType: undefined,
  selectedChainEntity: {},
  entityPricePolicyList: [],
  entityProposalPolicyList: [],
  selectedSettingPartner: {
    entityPricePolicy: {},
    entityProposalPolicy: {},
    entityPricePolicyID: '',
    entityProposalPolicyID: '',
    selectedProducts: [],
    selectAllProducts: true,
    categoryID: undefined,
    search: undefined,
    totalSelectedProduct: 0,
    groupID: '',
  },
  selectedSettingApprovePartner: {
    entityPricePolicy: {},
    entityProposalPolicy: {},
    entityPricePolicyID: '',
    entityProposalPolicyID: '',
    selectedProducts: [],
    selectAllProducts: true,
    categoryID: undefined,
    search: undefined,
    totalSelectedProduct: 0,
    groupID: '',
  },
  isLoadingSuggestSelectedEntityType: false,
  isLoadingRecentlySelectedEntityType: false,
};

const partnerSlice = createSlice({
  name: 'partner',
  initialState,
  reducers: {
    reloadPartner(state) {
      state.reloadFlag = !state.reloadFlag;
    },
    reloadPartnerNoEffect(state) {
      state.reloadFlagNotEffect = !state.reloadFlagNotEffect;
    },
    setSelectedSuggestEntityInChainType(state, action) {
      state.selectedSuggestEntityInChainType = action.payload;
    },
    setSelectedPartnerType(state, action) {
      state.selectedPartnerType = action.payload;
    },
    setSelectedChainEntityForCreate(state, action) {
      state.selectedChainEntity = action.payload;
    },
    setSelectedSettingPartner(state, action) {
      state.selectedSettingPartner = {
        ...state.selectedSettingPartner,
        ...action.payload,
      };
    },
    resetSelectSettingPartner(state) {
      state.selectedSettingPartner = {
        entityPricePolicy: {},
        entityProposalPolicy: {},
        entityPricePolicyID: '',
        entityProposalPolicyID: '',
        selectedProducts: [],
        selectAllProducts: true,
        categoryID: undefined,
        search: undefined,
        totalSelectedProduct: 0,
        groupID: '',
      };
    },
    setIsLoadingSuggestSelectedEntityType(state, action) {
      state.isLoadingSuggestSelectedEntityType = action.payload;
    },
    setIsLoadingRecentlySelectedEntityType(state, action) {
      state.isLoadingRecentlySelectedEntityType = action.payload;
    },
    setSelectedSettingPartnerEntityProposalPolicyID(state, action) {
      if (
        state.selectedSettingPartner &&
        state.selectedSettingPartner.entityProposalPolicyID
      ) {
        state.selectedSettingPartner.entityProposalPolicyID = action.payload;
      }
    },
    setSelectedSettingPartnerEntityPricePolicyID(state, action) {
      if (
        state.selectedSettingPartner &&
        state.selectedSettingPartner.entityPricePolicyID
      ) {
        state.selectedSettingPartner.entityPricePolicyID = action.payload;
      }
    },

    setSelectedSettingApprovePartner(state, action) {
      state.selectedSettingApprovePartner = {
        ...state.selectedSettingApprovePartner,
        ...action.payload,
      };
    },
  },
  extraReducers: builder => {
    builder
      .addCase(findPartnerForForecastQuantity.fulfilled, (state, action) => {
        const { data } = action.payload;

        const newData = data.map(item => {
          return {
            ...item,
            key: item.id,
            value: item.id,
          };
        });

        state.list = newData;
      })
      .addCase(findPartner.fulfilled, (state, action) => {
        const { data } = action.payload;

        const newData = data.map(item => {
          return {
            ...item,
            key: item.id,
            value: item.id,
          };
        });

        state.list = newData;
      })
      .addCase(findPartnerForMenuList.fulfilled, (state, action) => {
        const { data } = action.payload;

        const newData = data.map(item => {
          return {
            ...item,
            key: item.id,
            value: item.id,
          };
        });

        state.menuList = newData;
      })
      .addCase(findPartnerForForwardList.fulfilled, (state, action) => {
        const { data } = action.payload;

        const newData = data.map(item => {
          return {
            ...item,
            key: item.id,
            value: item.id,
          };
        });

        state.forwardList = newData;
      });
  },
});

const { actions, reducer: partnerReducer } = partnerSlice;

export const {
  reloadPartner,
  reloadPartnerNoEffect,
  setSelectedSuggestEntityInChainType,
  setSelectedPartnerType,
  setSelectedChainEntityForCreate,

  setSelectedSettingPartner,
  setSelectedSettingPartnerEntityProposalPolicyID,
  setSelectedSettingPartnerEntityPricePolicyID,
  setIsLoadingSuggestSelectedEntityType,
  setIsLoadingRecentlySelectedEntityType,
  resetSelectSettingPartner,

  setSelectedSettingApprovePartner,
} = actions;

export default partnerReducer;
